import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { SEO } from '../SEO';
import { ChatProvider, ChatWidget } from '../chat';
import AlioLogo from '../../assets/images/logo/Logo_DataDriven.png';
import { SmartLink, Btn } from './primitives';
import { useContent } from './content';
import LikeSite from './LikeSite';
import { Language, useLanguage } from '../../context/LanguageContext';
import './redesign.css';

const LANGUAGES: Language[] = ['en', 'es', 'pt'];

const LangSwitch: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { language, setLanguage } = useLanguage();
  const { langLabel } = useContent();
  return (
    <div className={`lang ${className}`} role="group" aria-label={langLabel}>
      {LANGUAGES.map((lang) => (
        <button
          key={lang}
          type="button"
          className={`lang__btn${language === lang ? ' on' : ''}`}
          aria-pressed={language === lang}
          onClick={() => setLanguage(lang)}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const RedesignNav: React.FC = () => {
  const { nav, navExtra, navStatus, navContact, menuLabel } = useContent();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <header className="nav">
        <SmartLink className="nav__logo" href="/">
          <img src={AlioLogo} alt="Alio IT Solutions" width={121} height={37} />
        </SmartLink>
        <nav className="nav__links" aria-label="Primary">
          {nav.map((item) => (
            <SmartLink key={item.href} href={item.href}>
              {item.label}
            </SmartLink>
          ))}
        </nav>
        <div className="nav__right">
          <span className="chip chip--ok">
            <i className="pulse" />
            {navStatus}
          </span>
          <LangSwitch className="lang--desk" />
          <Btn href="/#contact" small>
            {navContact}
          </Btn>
          <button
            className="nav__burger"
            type="button"
            aria-label={menuLabel}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            ≡
          </button>
        </div>
      </header>
      <div className={`nav__mobile${open ? ' open' : ''}`}>
        {[...nav, ...navExtra].map((item) => (
          <SmartLink key={item.href} href={item.href} onClick={close}>
            {item.label}
          </SmartLink>
        ))}
        <LangSwitch />
      </div>
    </>
  );
};

const RedesignFooter: React.FC = () => {
  const { footer } = useContent();
  return (
    <footer className="footer">
      <div className="wrap footer__grid">
        <div className="footer__brand">
          <img src={AlioLogo} alt="Alio IT Solutions" width={121} height={37} />
          <p className="footer__tag">{footer.tag}</p>
          <span className="chip chip--ok">
            <i className="pulse" />
            {footer.status}
          </span>
        </div>
        {footer.cols.map((col) => (
          <nav key={col.head} className="footer__col" aria-label={col.head.replace('//', '')}>
            <span className="footer__h">{col.head}</span>
            {col.links.map((link) => (
              <SmartLink key={link.href} href={link.href}>
                {link.label}
              </SmartLink>
            ))}
          </nav>
        ))}
      </div>
      <div className="wrap footer__legal">
        <span>{footer.legalA}</span>
        <span>{footer.legalB}</span>
      </div>
    </footer>
  );
};

const HOME_SEO = {
  title: 'Forward-Deployed AI Engineering for Regulated Teams | Alio IT',
  description:
    'Alio embeds senior engineers inside regulated, operationally complex organizations to ship AI agents, RAG and automations into production, then keeps them running with ManagedOps.',
  url: '/',
  schemaType: 'Organization' as const,
  keywords: [
    'Forward-deployed AI engineering',
    'AI agent development',
    'RAG implementation',
    'Managed engineering operations',
    'Nearshore software development',
    'DevOps consulting',
    'Healthcare interoperability FHIR',
    'Cybersecurity and vCISO',
    'QA automation',
    'Salesforce implementation',
  ],
};

const RedesignLayout: React.FC<{
  children: React.ReactNode;
  /** false skips the default homepage tags so the page can supply its own. */
  seo?: false | typeof HOME_SEO;
  /** Inner pages sit below the fixed nav. The homepage hero already accounts for it. */
  inner?: boolean;
}> = ({ children, seo, inner = false }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Data attributes, not classes: React rewrites className on re-render and would drop a reveal class.
    root.setAttribute('data-js', '');

    // Re-run on route and language so newly mounted nodes still get revealed.
    const observe = () => {
      const targets = Array.from(root.querySelectorAll('.rv:not([data-on])'));
      if (typeof IntersectionObserver === 'undefined') {
        targets.forEach((el) => el.setAttribute('data-on', ''));
        return () => undefined;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.setAttribute('data-on', '');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
      );
      targets.forEach((el) => io.observe(el));
      return () => io.disconnect();
    };

    // Children paint after this effect on client navigations; wait a frame.
    let disconnect = observe();
    const raf = requestAnimationFrame(() => {
      disconnect();
      disconnect = observe();
    });
    return () => {
      cancelAnimationFrame(raf);
      disconnect();
    };
  }, [language, pathname]);

  return (
    <ChatProvider>
      <ChatWidget autoOpenOnDesktop={false} />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&display=swap"
        />
      </Helmet>
      {seo !== false && <SEO {...(seo ?? HOME_SEO)} />}
      <div className="rd" ref={rootRef}>
        <div className="crt" aria-hidden="true" />
        <div className="bg-decor" aria-hidden="true">
          <span className="bg-line bg-line--v" style={{ left: '8%', top: '12%', height: '34%' }} />
          <span className="bg-line bg-line--h" style={{ left: '64%', top: '6%', width: '26%' }} />
          <span className="bg-line bg-line--v" style={{ left: '91%', top: '44%', height: '38%' }} />
          <span className="bg-line bg-line--h" style={{ left: '4%', top: '78%', width: '18%' }} />
          <span className="binary" style={{ left: '6%', top: '64%' }}>
            01000001 01001100 01001001 01001111
          </span>
          <span className="binary" style={{ left: '56%', top: '88%' }}>
            00101110 00101111 01010011 01001000 01001001 01010000
          </span>
        </div>
        <RedesignNav />
        <main id="top" className={inner ? 'main--inner' : undefined}>
          {children}
        </main>
        <RedesignFooter />
        <LikeSite />
      </div>
    </ChatProvider>
  );
};

export default RedesignLayout;
